import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/* The mobile sheet system — "every transient task is a sheet" (design pattern #02).
   One host renders at most one bottom sheet at a time with a scrim, a grabber, an
   entrance/exit transform, swipe-down-to-dismiss, scrim-tap dismiss, Esc, and a
   focus trap. Detents map to the design's three sizes:
     medium ≈ 45dvh  ·  large ≈ 85dvh  ·  full ≈ 100dvh (status bar visible)
   A sticky footer CTA is supported via <Sheet.Footer>. Sheets cover the tab bar
   (except the Menu tab, which is itself a tab). */

const SheetCtx = createContext(null);
export const useSheet = () => useContext(SheetCtx);

const DETENT_VH = { medium: 46, large: 85, full: 100 };

export function SheetProvider({ children }) {
  const [sheet, setSheet] = useState(null); // { render, detent, label, onClose }
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const closeSheet = useCallback(() => {
    setOpen(false);
    sheet?.onClose?.();
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setSheet(null), 320); // after exit transition
  }, [sheet]);

  const openSheet = useCallback((render, opts = {}) => {
    clearTimeout(closeTimer.current);
    setSheet({ render, detent: opts.detent || "large", label: opts.label || "", onClose: opts.onClose });
    // next frame → trigger the open transition
    requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <SheetCtx.Provider value={{ openSheet, closeSheet, isOpen: !!sheet }}>
      {children}
      {sheet && <SheetRender sheet={sheet} open={open} onClose={closeSheet} />}
    </SheetCtx.Provider>
  );
}

function SheetRender({ sheet, open, onClose }) {
  const panelRef = useRef(null);
  const dragRef = useRef({ startY: 0, dy: 0, dragging: false });
  const [dragY, setDragY] = useState(0);

  // Focus trap + Esc + body scroll lock.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const panel = panelRef.current;
    const focusable = () =>
      panel ? [...panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])')] : [];
    const first = focusable()[0];
    if (first) first.focus();
    else if (panel) panel.focus();

    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "Tab") {
        const f = focusable();
        if (!f.length) return;
        const idx = f.indexOf(document.activeElement);
        if (e.shiftKey && idx <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && idx === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose]);

  // Swipe-down-to-dismiss (from the grabber zone).
  const onPointerDown = (e) => { dragRef.current = { startY: e.clientY, dy: 0, dragging: true }; };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const dy = Math.max(0, e.clientY - dragRef.current.startY);
    dragRef.current.dy = dy;
    setDragY(dy);
  };
  const onPointerUp = () => {
    if (!dragRef.current.dragging) return;
    const dy = dragRef.current.dy;
    dragRef.current.dragging = false;
    if (dy > 110) onClose();
    setDragY(0);
  };

  const vh = DETENT_VH[sheet.detent] || 85;
  // Full sheets fill the viewport (status bar visible); medium/large use their vh.
  // (Previously "full" left height undefined → it collapsed to content height.)
  const panelStyle = {
    // env() not var(--m-safe-top): the panel mounts above .m-app, so --m-* tokens
    // aren't in scope here (that's what collapsed the "full" detent before).
    height: sheet.detent === "full" ? "calc(100dvh - env(safe-area-inset-top, 0px) - 8px)" : `${vh}dvh`,
    maxHeight: "calc(100dvh - env(safe-area-inset-top, 0px) - 8px)",
    transform: open ? `translateY(${dragY}px)` : undefined,
    transition: dragRef.current.dragging ? "none" : undefined,
  };

  return (
    <>
      <div className="m-sheet-scrim" data-open={open} onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="m-sheet-panel"
        data-open={open}
        role="dialog"
        aria-modal="true"
        aria-label={sheet.label || "Sheet"}
        tabIndex={-1}
        style={panelStyle}
      >
        <div
          className="m-grabber"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ touchAction: "none", cursor: "grab", width: 40, height: 5, marginTop: 8 }}
        />
        {sheet.render({ close: onClose })}
      </div>
    </>
  );
}

/* Layout helpers for sheet content. */
export function SheetBody({ children, style }) {
  return (
    <div className="m-no-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 16px 16px", ...style }}>
      {children}
    </div>
  );
}
export function SheetFooter({ children, style }) {
  return (
    <div style={{ flex: "none", padding: "10px 16px calc(16px + env(safe-area-inset-bottom, 0px))", borderTop: "1px solid #1F1F23", ...style }}>
      {children}
    </div>
  );
}
export function SheetTitle({ children, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 16px 10px" }}>
      <span style={{ font: "800 20px 'Manrope'", letterSpacing: "-.02em" }}>{children}</span>
      {right}
    </div>
  );
}

export default SheetProvider;
